/**
 * The web app's route catalog (IMM-322). Every in-app link is built through one
 * of these builders — the path shape lives in exactly ONE place, shared by the
 * web app (which renders the links) and the backend (which deep-links into the
 * app from emails). A route rename is a single edit here; nothing hand-types a
 * path string elsewhere.
 *
 * Builders are **workspace-scoped**: the first argument is always the workspace
 * URL slug (`composeWorkspaceSlug`), and the returned path carries the mandatory
 * `/w/:slug` prefix. There is no such thing as a "bare" catalog path — the prefix
 * cannot be forgotten, because every builder bakes it in.
 *
 * - In-app, bind the active workspace once via `useWorkspaceRoutes()` (web app)
 *   so call sites read `routes.contact(id)` rather than threading the slug.
 * - From outside the SPA (transactional emails, webhooks) prepend the origin:
 *   `` `${origin}${appRoutes.locationReview(slug, id)}` ``.
 *
 * The route *table* (`routes.tsx`) still owns the React Router pattern strings
 * (they bind to components and layouts); a guard test asserts every builder
 * below resolves against that table, so the two can never silently drift.
 */
export const appRoutes = {
  /** Workspace dashboard / home. */
  home: (slug: string) => `/w/${slug}`,
  /** HLRE's own area inside its workspace. */
  hlre: (slug: string) => `/w/${slug}/hlre`,

  // Locations (a.k.a. organisations / Standorte).
  locations: (slug: string) => `/w/${slug}/locations`,
  location: (slug: string, id: string) => `/w/${slug}/locations/${id}`,
  locationEdit: (slug: string, id: string) => `/w/${slug}/locations/${id}/edit`,
  /** Partner-onboarding review surface ("Einrichtung prüfen"). */
  locationReview: (slug: string, id: string) => `/w/${slug}/locations/${id}/review`,

  // Members.
  members: (slug: string) => `/w/${slug}/members`,
  member: (slug: string, id: string) => `/w/${slug}/members/${id}`,
  memberEdit: (slug: string, id: string) => `/w/${slug}/members/${id}/edit`,
  memberProfileEdit: (slug: string, id: string) => `/w/${slug}/members/${id}/profile/edit`,

  // Standalone workspace surfaces.
  /**
   * The Aktivitäten tab (IMM-776.3) — every open activity in the caller's
   * scope, merging the two bounded reads IMM-776.1 and IMM-776.2 shipped.
   * Replaces „Meine Aufgaben"; `tasks` below now redirects here.
   */
  activities: (slug: string) => `/w/${slug}/activities`,
  /**
   * „Meine Aufgaben" (IMM-430.4). The route stays in the catalog — and in
   * `routes.tsx` — purely as a redirect target (IMM-776.3): the table needs a
   * path to match, and the guard test below resolves every builder against it.
   */
  tasks: (slug: string) => `/w/${slug}/tasks`,
  chat: (slug: string) => `/w/${slug}/chat`,
  /**
   * Standalone mailbox (IMM-233.3). Posteingang is the mailbox landing — the
   * "E-Mails" nav row points here — and Postausgang is its sibling. The two were
   * one route with a client-side segment toggle; they are separate paths so each
   * is linkable, deep-linkable and back-navigable.
   */
  mailboxInbox: (slug: string) => `/w/${slug}/mail`,
  mailboxOutbox: (slug: string) => `/w/${slug}/mail/sent`,
  /**
   * An opened conversation, hanging directly off its segment. One route per
   * segment (rather than one shared thread route) so the page still knows which
   * list it belongs to — the breadcrumb and the close affordance both need it.
   * The id is the PROVIDER's thread id, an opaque vendor string, so it is
   * percent-encoded.
   *
   * `/mail/:providerThreadId` overlaps `/mail/sent` by shape; the router ranks the
   * static segment first, so the Postausgang always wins (asserted in the
   * route-table contract test). The cost is that a provider thread id of literally
   * "sent" would be unreachable — no real vendor id looks like that.
   */
  mailboxInboxThread: (slug: string, providerThreadId: string, accountId?: string) =>
    `/w/${slug}/mail/${encodeURIComponent(providerThreadId)}${accountId === undefined ? '' : `?account=${encodeURIComponent(accountId)}`}`,
  mailboxOutboxThread: (slug: string, providerThreadId: string, accountId?: string) =>
    `/w/${slug}/mail/sent/${encodeURIComponent(providerThreadId)}${accountId === undefined ? '' : `?account=${encodeURIComponent(accountId)}`}`,

  /**
   * The member's own calendar (IMM-234.2). Woche is the landing; the chosen view
   * lives in `sessionStorage`, not in the path.
   */
  calendar: (slug: string, createFrom?: string, label?: string) =>
    createFrom === undefined || label === undefined
      ? `/w/${slug}/calendar`
      : `/w/${slug}/calendar?${new URLSearchParams({ createFrom, label }).toString()}`,
  /**
   * The calendar opened on one appointment's day with its detail panel showing
   * (IMM-234.8). `on` is the local `YYYY-MM-DD` day the calendar anchors on.
   */
  calendarAppointment: (slug: string, appointmentId: string, on: string) =>
    `/w/${slug}/calendar?${new URLSearchParams({ appointment: appointmentId, on }).toString()}`,

  // Contacts.
  contacts: (slug: string, query?: string) => `/w/${slug}/contacts${searchSuffix(query)}`,
  contact: (slug: string, id: string) => `/w/${slug}/contacts/${id}`,

  // Companies.
  companies: (slug: string) => `/w/${slug}/companies`,
  company: (slug: string, id: string) => `/w/${slug}/companies/${id}`,

  // Properties.
  properties: (slug: string, query?: string) => `/w/${slug}/properties${searchSuffix(query)}`,
  // The Objekt detail page carries its active tab in the `?tab=` query param,
  // exactly like the Deal one below. The ⌘K palette's Dokumente group deep-links
  // here with `'documents'` (IMM-448.3): a document has no route of its own, so
  // its row is reached THROUGH its Objekt's Dokumente tab. The tab tokens must
  // match the web-app's `TAB_KEYS` (`property-detail.tsx`) exactly, or the parser
  // falls back to the overview tab.
  property: (
    slug: string,
    id: string,
    tab?: 'overview' | 'media' | 'documents' | 'activity' | 'contacts' | 'marketing',
  ): string => `/w/${slug}/properties/${id}${tab !== undefined ? `?tab=${tab}` : ''}`,

  // Owner leads.
  leads: (slug: string, query?: string) => `/w/${slug}/leads/owners${searchSuffix(query)}`,
  lead: (slug: string, id: string) => `/w/${slug}/leads/owners/${id}`,

  // Sales pipeline (Interessenten).
  mandates: (slug: string) => `/w/${slug}/mandates`,
  salesPipeline: (slug: string, query?: string) => `/w/${slug}/sales-pipeline${searchSuffix(query)}`,
  salesPipelineProspect: (slug: string, id: string) => `/w/${slug}/sales-pipeline/${id}`,

  // Acquisition pipeline (deals).
  deals: (slug: string, query?: string) => `/w/${slug}/acquisition-pipeline${searchSuffix(query)}`,
  // The Deal detail page has no tabs since IMM-456.2, so `?tab=` carries exactly
  // one LEGACY token: `'valuation'`. The Bewertung surface is a route of its own
  // (IMM-456.1) and the deal detail loader answers `?tab=valuation` with a replace
  // redirect to it. The token stays accepted because the IMM-407.6 „Bewertung
  // fertig" e-mail minted it into inboxes we cannot edit — nothing mints it any
  // more. `matchRoutes` ignores the query string, so the route table (and its
  // guard test) is unaffected.
  deal: (slug: string, acquisitionId: string, tab?: 'valuation') =>
    `/w/${slug}/acquisition-pipeline/${acquisitionId}${tab !== undefined ? `?tab=${tab}` : ''}`,
  /**
   * The Deal's Bewertung surface (IMM-456.1) — its own address, so it survives
   * IMM-456.2 removing the deal detail's tab strip. The URL segment is English
   * like every other route path; „Bewertung" lives only in the dictionary.
   */
  dealValuation: (slug: string, acquisitionId: string) => `/w/${slug}/acquisition-pipeline/${acquisitionId}/valuation`,
  /**
   * The Deal-Überblick (IMM-430.7) — the Standortleitung's cross-deal surface.
   *
   * A TOP-LEVEL route rather than `/acquisition-pipeline/overview`: nesting it
   * would put the literal segment `overview` in the same slot as `:acquisitionId`,
   * where a deal id and the word compete for the same match.
   */
  dealOverview: (slug: string, query?: string) => `/w/${slug}/deal-overview${searchSuffix(query)}`,
  /**
   * The member's cross-deal Bewertungen section (IMM-467). TOP-LEVEL for the same
   * reason `dealOverview` is: it spans deals rather than hanging off any single
   * one. The URL segment is English like every other route path; „Bewertungen"
   * lives only in the dictionary.
   */
  valuations: (slug: string, query?: string) => `/w/${slug}/valuations${searchSuffix(query)}`,
  valuation: (slug: string, id: string) => `/w/${slug}/valuations/${id}`,

  // Settings.
  settings: (slug: string) => `/w/${slug}/settings`,
  settingsMyProfile: (slug: string) => `/w/${slug}/settings/my-profile`,
  settingsMyProfileEdit: (slug: string) => `/w/${slug}/settings/my-profile/edit`,
  settingsCompany: (slug: string) => `/w/${slug}/settings/company`,
  settingsBranding: (slug: string) => `/w/${slug}/settings/branding`,
  settingsPortals: (slug: string) => `/w/${slug}/settings/portals`,
  settingsLeadPartners: (slug: string) => `/w/${slug}/settings/lead-partners`,
  // The handoff's entry point (IMM-751.4). English segment, German label in
  // i18n only — the linked application sends the member back here, so it is a
  // STABLE route and the crossing closes where it opened.
  settingsPrintMaterial: (slug: string) => `/w/${slug}/settings/print-material`,
  settingsAdmins: (slug: string) => `/w/${slug}/settings/admins`,
  settingsMailbox: (slug: string) => `/w/${slug}/settings/mailbox`,
  settingsCompanyMailboxes: (slug: string) => `/w/${slug}/settings/company-mailboxes`,
  settingsApiKeys: (slug: string) => `/w/${slug}/settings/api-keys`,
  settingsDocumentTemplates: (slug: string, tab?: 'email') =>
    `/w/${slug}/settings/templates${tab !== undefined ? `?tab=${tab}` : ''}`,
  settingsNewEmailTemplate: (slug: string) => `/w/${slug}/settings/templates/email/new`,
  settingsEmailTemplate: (slug: string, id: string) => `/w/${slug}/settings/templates/email/${id}`,
  settingsDocumentTemplate: (slug: string, id: string) => `/w/${slug}/settings/templates/${id}`,
  settingsBuyerContractTemplateAdapt: (slug: string) => `/w/${slug}/settings/templates/buyer-contract/adapt`,
  settingsBuyerContractTemplate: (slug: string, id: string) => `/w/${slug}/settings/templates/buyer-contract/${id}`,
  settingsDocumentTemplateNew: (slug: string, draftId: string) => `/w/${slug}/settings/templates/new/${draftId}`,
  settingsMcp: (slug: string) => `/w/${slug}/settings/mcp`,
  settingsSkills: (slug: string) => `/w/${slug}/settings/skills`,
} as const;

/**
 * The catalog with the workspace slug already bound — every builder drops its
 * leading `slug` argument (`routes.contact(id)` → `/w/:slug/contacts/:id`).
 * This is what `useWorkspaceRoutes()` hands components.
 */
export type WorkspaceRoutes = {
  [K in keyof typeof appRoutes]: (typeof appRoutes)[K] extends (slug: string, ...args: infer A) => string
    ? (...args: A) => string
    : never;
};

/**
 * Bind a workspace slug to every catalog builder, returning slug-free builders.
 * Pure (no framework) so it is shared by the web-app hook and is unit-testable;
 * the web app memoises the result per active workspace.
 */
export function withWorkspace(slug: string): WorkspaceRoutes {
  const bound: Record<string, (...args: string[]) => string> = {};

  for (const [key, build] of Object.entries(appRoutes)) {
    bound[key] = (...args: string[]) => (build as (...a: string[]) => string)(slug, ...args);
  }

  return bound as WorkspaceRoutes;
}

/**
 * The `?q=` suffix a list route carries when the ⌘K palette's „Alle Treffer in
 * {Bereich}" row opens it pre-filtered (IMM-448.4). The query lives in the URL
 * — not in navigation state — so the filtered list survives a reload and can be
 * pasted to a colleague; the list page reads it back and renders it as its
 * „Suche: {query}" chip. A blank term is the same as none.
 */
function searchSuffix(query?: string): string {
  const trimmed = query?.trim();

  return trimmed === undefined || trimmed === '' ? '' : `?q=${encodeURIComponent(trimmed)}`;
}
